import { Star } from 'lucide-react';
import { useEffect, type ReactNode } from 'react';
import { cn } from '../lib/cn';

export interface PageHeaderProps {
  title: ReactNode;
  subtitle?: ReactNode;
  breadcrumb?: ReactNode;
  actions?: ReactNode;
  back?: ReactNode;
  className?: string;
  /**
   * Star affordance. When `onToggleStar` is provided, a star icon
   * renders on the right edge of the title row + a ⌘D shortcut is
   * installed for the lifetime of this header. `isStarred` controls
   * the filled state. Both props must come together; passing one
   * without the other is a usage error.
   *
   * Wiring (read/write the favorite row + resolve current route +
   * look up module/workspace metadata) lives in the app layer.
   * PageHeader is purely presentation.
   */
  isStarred?: boolean;
  onToggleStar?: () => void;
}

export function PageHeader({
  title,
  subtitle,
  breadcrumb,
  actions,
  back,
  className,
  isStarred,
  onToggleStar,
}: PageHeaderProps) {
  // ⌘D / Ctrl+D toggles the star while this header is mounted. Browser
  // intercepts ⌘D for "bookmark this page" by default; preventDefault
  // claims it. Skip when the user is typing inside a field so the
  // shortcut doesn't clobber in-flight composition.
  useEffect(() => {
    if (!onToggleStar) return;
    const handler = (e: KeyboardEvent) => {
      const isToggle = (e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'd';
      if (!isToggle) return;
      const target = e.target as HTMLElement | null;
      const tag = target?.tagName;
      if (target?.isContentEditable || tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') {
        return;
      }
      e.preventDefault();
      onToggleStar();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [onToggleStar]);

  return (
    <header className={cn('mb-8 flex flex-col gap-3', className)}>
      {breadcrumb ? <div className="text-caption text-text-secondary">{breadcrumb}</div> : null}
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="flex min-w-0 items-start gap-3">
          {back ? <div className="mt-1">{back}</div> : null}
          <div className="flex min-w-0 flex-col gap-1">
            <h1 className="flex items-center gap-2 truncate text-h1 tracking-tight text-text-primary">
              <span className="truncate">{title}</span>
              {onToggleStar ? (
                <button
                  type="button"
                  onClick={onToggleStar}
                  aria-label={isStarred ? 'Remove from favorites (⌘D)' : 'Add to favorites (⌘D)'}
                  aria-pressed={isStarred}
                  title={isStarred ? 'Remove from favorites (⌘D)' : 'Add to favorites (⌘D)'}
                  className={cn(
                    'shrink-0 rounded-md p-1 transition-colors',
                    isStarred
                      ? 'text-accent-500 hover:text-accent-600'
                      : 'text-text-muted hover:bg-bg-elevated hover:text-accent-500',
                  )}
                >
                  <Star className={cn('h-5 w-5', isStarred && 'fill-current')} aria-hidden="true" />
                </button>
              ) : null}
            </h1>
            {subtitle ? <p className="text-body text-text-secondary">{subtitle}</p> : null}
          </div>
        </div>
        {actions ? (
          <div className="flex w-full min-w-0 flex-wrap items-start gap-2 md:w-auto md:shrink-0 md:items-center">
            {actions}
          </div>
        ) : null}
      </div>
    </header>
  );
}
